// useDestinationAddress.ts
import { useEffect } from 'react'

import { ChainId } from '@src/configs/chainInfo'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { useExchangeState } from '@src/pages/Exchange/context/ExchangeStateContext'
import { EVMAddress, SolanaAddress, TronAddress } from '@src/types/token'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('useDestinationAddress')

interface UseDestinationAddressProps {
    isCrossChain: boolean
    buyData: {
        chainId: number
    }
    sellData: {
        chainId: number
    }
    evmWalletAddress?: EVMAddress
    solanaWalletAddress?: SolanaAddress
    tronWalletAddress?: TronAddress
}

export function useDestinationAddress({
    isCrossChain,
    buyData,
    sellData,
    evmWalletAddress,
    solanaWalletAddress,
    tronWalletAddress,
}: UseDestinationAddressProps) {
    const { destinationAddress, setDestinationAddress } = useExchangeDataContext()
    const { isCustomRecipientWallet } = useExchangeState()

    useEffect(() => {
        // Only set default destination address if custom recipient is not enabled
        if (!isCustomRecipientWallet) {
            logger.debug('[useDestinationAddress] Setting default address for chain', {
                chainId: buyData.chainId,
                isCustomRecipientWallet,
            })

            // Set appropriate destination address based on chain type
            switch (buyData.chainId) {
                case ChainId.Solana:
                    if (solanaWalletAddress) {
                        setDestinationAddress(solanaWalletAddress)
                    }
                    break
                case ChainId.Tron:
                    if (tronWalletAddress) {
                        setDestinationAddress(tronWalletAddress)
                    }
                    break
                default: // EVM chains
                    if (evmWalletAddress) {
                        setDestinationAddress(evmWalletAddress)
                    }
                    break
            }
        } else {
            logger.debug('[useDestinationAddress] Keeping custom address', {
                isCustomRecipientWallet,
                destinationAddress,
            })
        }
    }, [
        isCrossChain,
        buyData.chainId,
        sellData.chainId,
        evmWalletAddress,
        solanaWalletAddress,
        tronWalletAddress,
        isCustomRecipientWallet,
    ])

    return {
        destinationAddress,
        setDestinationAddress,
    }
}
